/**
 * Leverage & capitalization — what a lender reads off a capital stack.
 *
 * The Capital Stack tab used to draw the same single senior tranche five ways
 * (a table, a column, a ring, a waterfall and a radial gauge) and the Overview
 * a sixth, while the figures a credit reader actually sizes a loan on — LTV or
 * LTC, debt yield, DSCR, loan per unit, the sponsor's equity — were missing or
 * a tab away. This module reduces a deal to those figures, once, for both
 * surfaces:
 *
 *   - the capitalization: each tranche's dollars, share and attachment /
 *     detachment points (Senior 0–75.0% LTC), the equity that closes it, and
 *     each tranche per unit or square foot;
 *   - the leverage and coverage figures, each with the operands it is made of.
 *
 * Every figure is either a value the deal states (a catalogue field, which may
 * be the sponsor's own model), the engine's derived value for an empty field
 * (the calculations service, with its formula), or a ratio of two stated
 * figures computed here and labelled Derived with its formula. Nothing is
 * filled in: a figure whose inputs are missing is not shown, and one short
 * line names what it is waiting on.
 *
 * Rounding (chosen on the owner's behalf, stated in the hand-off):
 *   - leverage ratios (LTV, LTC, attachment points): one decimal, 75.0%;
 *   - yields (debt yield, cap rate): two decimals, 7.46%;
 *   - DSCR: two decimals, 1.31x;
 *   - per-unit figures: whole dollars; amounts: the dossier's formatAmount.
 */

import { asNumber, formatAmount, formatMultiple, formatPoints } from './format'
import { SENIOR_NOT_STATED, stackPosition } from './dealTerms'
import { impliedLtv, ltvWithImplied } from './valueBasis'
import type { SlotKind } from './editableFields'
import { impliedEquityFormula, impliedEquityInputs, type CapitalStackModel, type LayerKind, type StackLayer } from './capitalStackModel'

/**
 * The engine's figure for a field, with its formula. Served to every reader of
 * the deal (UI-03), so an approver and an analyst see one number.
 *
 * `fromInputs` is what the deal's current inputs give for the rule, and
 * `matchesInputs` whether the value the deal holds follows from them at its own
 * stated precision — the server's one tolerance. A stated figure the inputs do
 * not reproduce is a variance, shown for information (R4).
 */
export type CalculatedFigure = {
  value: number
  formula: string | null
  fromInputs?: number | null
  matchesInputs?: boolean
}
export type Calculated = (name: string) => CalculatedFigure | undefined

/** A maturity the closing date and the term do not reproduce (DER-11): the stated
 *  date stands, the computed one is shown beside it, for information. */
export type MaturityVariance = { stated: string; computed: string; note: string }

/** Coverage the platform computes beside the stated DSCR, never in place of it. */
export type CoverageExtra = {
  id: 'dscr_amortizing' | 'dscr_io_indicative'
  label: string
  display: string
  value: number
  /** `suggested`: the figure rests on an indicative rate the platform priced from a dated SOFR print. */
  basis: FigureBasis | 'suggested'
  field: string
  formula: string
  /** The dated basis of a Suggested figure, in words. */
  note?: string
  inputs: string[]
}

/** How a figure was reached. `stated`: a catalogue field holds it (its own
 *  lineage then says whether a document, a person or the engine wrote it).
 *  `derived`: computed — by the engine for an empty field, or here. */
export type FigureBasis = 'stated' | 'derived'

export type LeverageId =
  | 'ltv'
  | 'ltc'
  | 'debt_yield'
  | 'dscr'
  | 'loan_per_unit'
  | 'basis_per_unit'
  | 'cap_rate'
  | 'equity'
  | 'total_cap'

export type LeverageFigure = {
  id: LeverageId
  label: string
  /** The figure as printed; '—' when it cannot be shown. */
  display: string
  value?: number
  basis?: FigureBasis
  /** The catalogue field the figure IS, when one holds it — so it carries
   *  that field's value state and sponsor attribution. */
  field?: string
  /** A derived figure's formula, in words ("loan ÷ purchase price"). */
  formula?: string
  /** The operands, as they read on the deal ("$132M ÷ $176M purchase price"). */
  operands?: string
  /** The figure is implied rather than recorded (residual equity). */
  implied?: boolean
  /** Why it is blank, in one short phrase ("needs NOI"). */
  waiting?: string
  /** The catalogue fields a person edits to change it. */
  inputs: string[]
}

export type CapSegment = {
  id: string
  label: string
  kind: LayerKind
  amount: number
  /** Share of the total, 0–100. */
  share: number
  /** Where the tranche starts and ends in the stack, 0–100. */
  attach: number
  detach: number
  isDebt: boolean
  isImplied: boolean
  isRequested: boolean
  /** The fill, by position: debt in one blue ramp by seniority, equity in one
   *  amber ramp (validated together on the dossier surface; see SEGMENT_COLORS). */
  color: string
  /** Text that sits on the fill: ink chosen by the fill's luminance. */
  ink: string
  perUnit?: string
  /** The catalogue field the amount is, or the formula of a computed one. */
  amountField?: string
  amountFormula?: string
  /** The catalogue fields a computed amount is made of (its inputs). */
  amountInputs?: string[]
  /** Why the amount is not edited in place: a stored tranche no field holds. */
  readOnlyReason?: string
  status: string | null
  rateLabel: string | null
  /** A rate parsed from a structure (an all-in rate) when none is stated. */
  rate?: number
  /** The catalogue field the rate was read from, so it carries its state. */
  rateField?: string
  term: string | null
  lender: string | null
}

export type Capitalization = {
  total: number
  /** What the total is: the purchase price, the uses, the value or the stack. */
  totalLabel: string
  /** What the attachment points are a ratio of. */
  ratio: 'LTC' | 'LTV' | null
  totalImplied: boolean
  segments: CapSegment[]
  /** The total per unit or square foot, when the deal states the count. */
  totalPerUnit?: string
  /** The catalogue field the total IS (the purchase price, the project cost,
   *  the value), so it carries that field's state and whose statement it is. */
  totalField?: string
}

export type LeverageModel = {
  loanAmount?: number
  /** Every tranche the deal records, whether or not a total is known, with
   *  its amount per unit or square foot where the deal states the count. */
  tranches: Array<StackLayer & { perUnit?: string }>
  capitalization: Capitalization | null
  figures: Record<LeverageId, LeverageFigure>
  /** The one line naming what the blank figures are waiting on, or null. */
  waiting: string | null
  /** Amortizing and indicative interest-only coverage the deal supports, beside the DSCR. */
  coverageExtras: CoverageExtra[]
  /** The stated maturity against the one the closing date and term give, when they differ. */
  maturityVariance: MaturityVariance | null
  /** "unit", "key", "SF" … what the per-unit figures divide by. */
  perNoun: string | null
  /** The stack's own note (what closed it, what is implied). */
  disclosure: string
}

/**
 * Tranche fills on the dossier's dark surface (#0d0d0d), chosen by POSITION in
 * the stack, never by rank among whatever is shown. Debt takes a blue ordinal
 * ramp by seniority and equity an amber one, so neighbors differ in lightness
 * inside a family and in hue across the debt/equity line. Validated with the
 * data-viz palette checks: each ramp passes the ordinal gate (monotone
 * lightness, visible steps, light end clear of the surface) and the debt/equity
 * pair separates under color-blind simulation by ΔE ≥ 24. The tranche table
 * beside the bar carries every name, so color is never the only identity.
 */
export const DEBT_STEPS = ['#3987e5', '#256abf', '#184f95'] as const
export const EQUITY_STEPS = ['#c98500', '#e8b04a'] as const

/** Ink for text set on a fill: dark on the light steps, light on the dark. */
function inkFor(color: string): string {
  return color === '#256abf' || color === '#184f95' ? '#f5f5f4' : '#0a0a0a'
}

function pct1(value: number): string {
  return `${value.toFixed(1)}%`
}

/** What a per-unit figure is quoted per, by the deal's unit of measure (the
 *  client's list: Acre, Beds, Keys, Pads, Rooms, Parking Sp, sf, sm, Units,
 *  NAP; and the Helios legacy Acres / SF). Every other measure used to read
 *  "unit": a 20-acre land loan printed "Loan / unit $500,000/unit". */
const PER_UNIT_NOUN: Record<string, string> = {
  units: 'unit',
  unit: 'unit',
  rooms: 'key',
  keys: 'key',
  beds: 'bed',
  pads: 'pad',
  acre: 'acre',
  acres: 'acre',
  'parking sp': 'space',
  'parking spaces': 'space',
}

/** The noun a per-unit figure divides by, and the count. Units / keys / beds
 *  when the deal states them (what a multifamily or hotel loan is quoted on),
 *  else rentable square feet. */
function perUnitBasis(get: (name: string) => unknown): { count: number; noun: string } | null {
  const units = asNumber(get('units'))
  const measure = String(get('unit_of_measure') ?? '').trim().toLowerCase()
  // Square metres are an area, and the platform converts nothing to SF (the
  // engine's AREA_MEASURES; dealTerms.unitCountLabel refuses `sm` too), so no
  // count here is divided into a per-unit or per-SF figure.
  if (measure === 'sm') return null
  // `NAP`: no unit of measure applies, so `units` counts nothing; a stated
  // square-foot area still does.
  if (units != null && units > 0 && measure !== 'sf' && measure !== 'nap') {
    return { count: units, noun: PER_UNIT_NOUN[measure] ?? 'unit' }
  }
  const sf = asNumber(get('sf')) ?? asNumber(get('square_feet'))
  if (sf != null && sf > 0) return { count: sf, noun: 'SF' }
  return null
}

/** Why there is no per-unit figure, for the waiting line. */
function perUnitWaiting(get: (name: string) => unknown): string {
  const measure = String(get('unit_of_measure') ?? '').trim().toLowerCase()
  if (measure === 'sm') return 'not divided: the deal is measured in square meters'
  if (measure === 'nap') return 'not divided: no unit of measure applies (NAP)'
  return 'needs units or SF'
}

function perUnitText(amount: number, basis: { count: number; noun: string }): string {
  const each = amount / basis.count
  // A per-SF figure keeps cents under $100 ($85.40/SF); per unit is whole dollars.
  const shown = basis.noun === 'SF' && each < 100 ? each.toFixed(2) : Math.round(each).toLocaleString('en-US')
  return `$${shown}/${basis.noun}`
}

function unitWords(basis: { count: number; noun: string }): string {
  const count = basis.count.toLocaleString('en-US')
  if (basis.noun === 'SF') return `${count} SF`
  return `${count} ${basis.noun}${basis.count === 1 ? '' : 's'}`
}

/** A percent catalogue field on the 0–100 scale ('ltc', 'debt_yield'). */
function points(value: unknown): number | undefined {
  return asNumber(value)
}

/** The native `ltv` column holds a fraction; the engine states points. */
function ltvPoints(value: number): number {
  return Math.abs(value) <= 1.5 ? value * 100 : value
}

export function buildLeverage(input: {
  /** The deal's stated values (the dossier's effective value, drafts included). */
  get: (name: string) => unknown
  /** The engine's derived value for a field the deal leaves empty. */
  calculated?: Calculated
  /** The formula of a value the deal HOLDS that is itself computed — the
   *  dossier's own derivation preview, or a field whose lineage is Derived —
   *  or null for a value a document or a person stated. A held figure is not
   *  "as stated" merely because a field holds it: Washington Square's debt
   *  yield is NOI ÷ loan, computed, sitting in the field. */
  heldDerived?: (name: string) => string | null
  /** The one-line dated source of a field's value, from its lineage ("SOFR 4.30%
   *  (NY Fed overnight print, 2026-11-03) + 350 bps"), or null. Said beside the
   *  indicative rate's coverage so a reader sees which print priced it. */
  basisOf?: (name: string) => string | null
  /** The variances `/calculations` served (DER-11): a stated date the terms do not reproduce. */
  variances?: Record<string, { stated: string; computed: string; note: string } | undefined>
  stack: CapitalStackModel
}): LeverageModel {
  const { get, stack } = input
  const calculated: Calculated = input.calculated ?? (() => undefined)
  const heldDerived = input.heldDerived ?? (() => null)
  /** A stated-branch figure, or the derived one it really is. */
  const held = (figure: LeverageFigure): LeverageFigure => {
    const formula = figure.field ? heldDerived(figure.field) : null
    return formula == null ? figure : { ...figure, basis: 'derived', field: undefined, formula }
  }
  const loan = asNumber(get('loan_amount'))
  const value = stack.propertyValue
  const price = stack.purchasePrice
  const totalCost = stack.totalCost
  const noi = asNumber(get('noi'))
  const debtService = asNumber(get('annual_debt_service'))
  const unitBasis = perUnitBasis(get)
  const purpose = String(get('purpose') ?? '').toLowerCase()

  // ── Capitalization ────────────────────────────────────────────────────────
  // Drawn only against a known total — debt and the equity that closes it.
  // With no total (the loan alone, or a note whose senior debt is not stated)
  // the tranches are still listed, with their dollars, but no share is
  // printed: a share of an unknown whole drew the loan as 100% of the stack.
  let capitalization: Capitalization | null = null
  if (stack.totalCap != null && stack.totalCap > 0 && stack.layers.length > 0) {
    let running = 0
    let debtIndex = 0
    let equityIndex = 0
    const segments = stack.layers.map((layer) => {
      const color = layer.isDebt
        ? DEBT_STEPS[Math.min(debtIndex++, DEBT_STEPS.length - 1)]
        : EQUITY_STEPS[Math.min(equityIndex++, EQUITY_STEPS.length - 1)]
      const attach = (running / (stack.totalCap as number)) * 100
      running += layer.amount
      const detach = (running / (stack.totalCap as number)) * 100
      const segment: CapSegment = {
        id: layer.id,
        label: layer.position,
        kind: layer.kind,
        amount: layer.amount,
        share: detach - attach,
        attach,
        detach,
        isDebt: layer.isDebt,
        isImplied: layer.isImplied,
        isRequested: layer.isRequested,
        color,
        ink: inkFor(color),
        perUnit: unitBasis ? perUnitText(layer.amount, unitBasis) : undefined,
        amountField: layer.amountField ?? (layer.id === 'residual-equity' && stack.residualSource === 'equity_field' ? 'total_equity' : undefined),
        amountFormula: layer.amountDerived ?? (layer.isImplied ? impliedFormula(stack) : undefined),
        amountInputs: layer.amountInputs ?? (layer.isImplied ? impliedInputs(stack) : undefined),
        readOnlyReason: layer.readOnlyReason,
        status: layer.status,
        rateLabel: layer.rateLabel,
        rate: layer.rate,
        rateField: layer.rateField,
        term: layer.term,
        lender: layer.lender,
      }
      return segment
    })
    // A stack closed against the value is the value only when nothing is
    // drawn beyond it. Future funding is advanced after closing, so value plus
    // the advances is not the value (the 'ff-value' shape: a $37.7M value read
    // "Value (As-Is) $40,600,000" under the value's own chip, and the senior's
    // 60.3% "LTV" beside a 65.0% LTV figure). That total is the stack's sum.
    const isValue = stack.residualSource === 'property_value' && !beyondValue(stack)
    capitalization = {
      total: stack.totalCap,
      totalLabel: isValue || stack.residualSource !== 'property_value' ? totalLabelFor(stack) : 'Total Capitalization',
      ratio: stack.residualSource === 'uses' ? 'LTC' : isValue ? 'LTV' : null,
      totalImplied: stack.totalImplied,
      segments,
      totalPerUnit: unitBasis ? perUnitText(stack.totalCap, unitBasis) : undefined,
      totalField:
        isValue
          ? 'property_value'
          : stack.residualSource === 'uses'
            ? stack.usesBasis === 'the purchase price' ? 'purchase_price' : stack.usesBasis === 'the total project cost' ? 'total_cost' : undefined
            : undefined,
    }
  }

  // ── LTV ───────────────────────────────────────────────────────────────────
  // Stated first, with the LTV the loan and value imply beside it when the
  // two differ (R3 keeps the stated ratio, R4 shows the gap). Never a value
  // back-solved from the stated LTV.
  //
  // On a subordinate note the loan is the note, so a computed LTV is the
  // note's dollars over the value — not leverage through the note, which the
  // stack's attachment column states (Stanhope Row: "LTV 16.0%" beside
  // Mezzanine 50.0–66.0% LTV). It says so in its label and formula, and a
  // stated LTV (a document's, likely through the note) is not marked against
  // the note-only figure. Whether the dossier should instead print the
  // cumulative LTV through the note is the owner's decision, still open.
  const subordinate = stackPosition(get).subordinate
  const statedLtv = asNumber(get('ltv'))
  const engineLtv = calculated('ltv')
  // The LTV the loan and value imply, beside a STATED one that differs: the
  // server's computation of it, judged against the stated precision by its one
  // tolerance — the same answer for every role (UI-03). `null`: the server says
  // the stated figure follows from the inputs, so there is nothing to show beside
  // it. `undefined`: the server returned nothing usable, and only then is the
  // browser's own division the answer.
  const servedImplied: number | null | undefined =
    statedLtv != null && engineLtv
      ? engineLtv.matchesInputs === true
        ? null
        : engineLtv.matchesInputs === false && engineLtv.fromInputs != null
          ? engineLtv.fromInputs
          : undefined
      : undefined
  const derivedLtv = impliedLtv(loan, value)
  const ltvFigure: LeverageFigure = statedLtv != null
    ? held({
        id: 'ltv', label: 'LTV', basis: 'stated', field: 'ltv', value: ltvPoints(statedLtv),
        display: ltvWithImplied(statedLtv, subordinate ? undefined : loan, value, subordinate ? null : servedImplied),
        operands: value != null && loan != null ? `${formatAmount(loan)} ÷ ${formatAmount(value)} value (as-is)` : undefined,
        inputs: ['ltv', 'loan_amount', 'property_value'],
      })
    : engineLtv
      ? {
          id: 'ltv', label: 'LTV', basis: 'derived', value: ltvPoints(engineLtv.value), display: pct1(ltvPoints(engineLtv.value)),
          formula: engineLtv.formula ?? 'loan ÷ value', inputs: ['loan_amount', 'property_value'],
        }
      : derivedLtv != null
        ? {
            id: 'ltv', label: 'LTV', basis: 'derived', value: derivedLtv, display: pct1(derivedLtv),
            formula: 'loan ÷ value (as-is)',
            operands: `${formatAmount(loan)} ÷ ${formatAmount(value)} value (as-is)`,
            inputs: ['loan_amount', 'property_value'],
          }
        : { id: 'ltv', label: 'LTV', display: '—', waiting: loan == null ? 'needs a loan amount' : 'needs a value (as-is)', inputs: ['loan_amount', 'property_value'] }
  const ltv: LeverageFigure = subordinate && ltvFigure.basis === 'derived'
    ? {
        ...ltvFigure,
        label: 'LTV (note)',
        formula: 'note ÷ value (as-is)',
        operands: loan != null && value != null ? `${formatAmount(loan)} note ÷ ${formatAmount(value)} value (as-is)` : ltvFigure.operands,
      }
    : ltvFigure

  // ── LTC ───────────────────────────────────────────────────────────────────
  // On the total project cost when stated, else on what the stated uses total
  // (the purchase price on a plain acquisition) — and the formula says which.
  const statedLtc = points(get('ltc'))
  const engineLtc = calculated('ltc')
  //
  // Every derived LTC says what it was made of (the tile's "Made of" line):
  // a refinance's LTC read off a Total Uses that had added the schedule's own
  // total to its lines ($127M for $63.5M, Magnolia Apartments, 2 Nov 2026)
  // printed a ratio no reader could trace to a figure on the page.
  const usesFields = stack.uses.flatMap((row) => (row.field ? [row.field] : []))
  const costBasis: { amount: number; words: string; inputs: string[] } | null = totalCost != null
    ? { amount: totalCost, words: 'total project cost', inputs: ['total_cost'] }
    : stack.usesComplete && stack.useTotal != null
      ? stack.usesBasis === 'the purchase price'
        ? { amount: stack.useTotal, words: 'purchase price', inputs: ['purchase_price'] }
        : {
            amount: stack.useTotal,
            // A stored schedule's total is the one it states; otherwise the
            // uses' sum.
            words: stack.usesStatedTotal ? 'total uses (as the schedule states it)' : 'total uses',
            inputs: usesFields,
          }
      : price != null
        ? { amount: price, words: 'purchase price', inputs: ['purchase_price'] }
        : null
  const ltc: LeverageFigure = statedLtc != null
    ? held({ id: 'ltc', label: 'LTC', basis: 'stated', field: 'ltc', value: statedLtc, display: formatPoints(statedLtc), inputs: ['ltc', 'loan_amount', 'total_cost'] })
    : engineLtc
      ? {
          id: 'ltc', label: 'LTC', basis: 'derived', value: engineLtc.value, display: pct1(engineLtc.value),
          formula: engineLtc.formula ?? 'loan ÷ total cost',
          operands: loan != null && totalCost != null ? `${formatAmount(loan)} ÷ ${formatAmount(totalCost)} total project cost` : undefined,
          inputs: ['loan_amount', 'total_cost'],
        }
      : loan != null && costBasis && costBasis.amount > 0
        ? {
            id: 'ltc', label: 'LTC', basis: 'derived', value: (loan / costBasis.amount) * 100,
            display: pct1((loan / costBasis.amount) * 100),
            formula: `loan ÷ ${costBasis.words}`,
            operands: `${formatAmount(loan)} ÷ ${formatAmount(costBasis.amount)} ${costBasis.words}`,
            inputs: ['loan_amount', ...costBasis.inputs],
          }
        : { id: 'ltc', label: 'LTC', display: '—', waiting: loan == null ? 'needs a loan amount' : 'needs a purchase price or project cost', inputs: ['loan_amount', 'purchase_price', 'total_cost'] }

  // ── Debt yield ────────────────────────────────────────────────────────────
  // In-place NOI over the loan. On a note whose senior debt is not stated it
  // is not shown: NOI over the note alone ignores the debt ahead of it.
  //
  // Nor on any subordinate note, senior stated or not. Its debt yield runs
  // through the debt ahead of it, which the catalogue has no field for, so
  // the engine withholds it for every subordinate note (dealfields
  // `_THROUGH_TRANCHE_TARGETS`); computing NOI ÷ note here put back the figure
  // the engine refuses (Stanhope Row: $1.95M ÷ $9.6M, 20.31%, about four
  // times safer than the position). A computed one a field still holds is
  // withheld too; only a debt yield a document states stands, as stated.
  const throughSenior: LeverageFigure = {
    id: 'debt_yield', label: 'Debt Yield', display: '—',
    waiting: 'runs through the senior debt ahead of this note', inputs: ['noi', 'loan_amount'],
  }
  const statedDy = points(get('debt_yield'))
  const engineDy = subordinate ? undefined : calculated('debt_yield')
  const statedDyFigure: LeverageFigure | null = statedDy != null
    ? held({
        id: 'debt_yield', label: 'Debt Yield', basis: 'stated', field: 'debt_yield', value: statedDy, display: formatPoints(statedDy),
        operands: noi != null && loan != null ? `${formatAmount(noi)} NOI ÷ ${formatAmount(loan)} loan` : undefined,
        inputs: ['noi', 'loan_amount'],
      })
    : null
  const debtYield: LeverageFigure = stack.seniorUnstated
    ? { id: 'debt_yield', label: 'Debt Yield', display: SENIOR_NOT_STATED, waiting: 'senior debt not stated', inputs: ['noi', 'loan_amount'] }
    : statedDyFigure
      ? subordinate && statedDyFigure.basis === 'derived' ? throughSenior : statedDyFigure
      : subordinate
        ? throughSenior
        : engineDy
          ? {
              id: 'debt_yield', label: 'Debt Yield', basis: 'derived', value: engineDy.value, display: formatPoints(engineDy.value),
              formula: engineDy.formula ?? 'NOI ÷ loan',
              operands: noi != null && loan != null ? `${formatAmount(noi)} NOI ÷ ${formatAmount(loan)} loan` : undefined,
              inputs: ['noi', 'loan_amount'],
            }
          : noi != null && loan != null && loan > 0
            ? {
                id: 'debt_yield', label: 'Debt Yield', basis: 'derived', value: (noi / loan) * 100, display: formatPoints((noi / loan) * 100),
                formula: 'NOI ÷ loan', operands: `${formatAmount(noi)} NOI ÷ ${formatAmount(loan)} loan`, inputs: ['noi', 'loan_amount'],
              }
            : { id: 'debt_yield', label: 'Debt Yield', display: '—', waiting: loan == null ? 'needs a loan amount' : 'needs NOI', inputs: ['noi', 'loan_amount'] }

  // ── DSCR ──────────────────────────────────────────────────────────────────
  const statedDscr = asNumber(get('dscr'))
  const engineDscr = calculated('dscr')
  const coverageOperands = noi != null && debtService != null ? `${formatAmount(noi)} NOI ÷ ${formatAmount(debtService)} debt service` : undefined
  const dscr: LeverageFigure = statedDscr != null
    ? held({ id: 'dscr', label: 'DSCR', basis: 'stated', field: 'dscr', value: statedDscr, display: formatMultiple(statedDscr), operands: coverageOperands, inputs: ['noi', 'annual_debt_service'] })
    : engineDscr
      ? {
          id: 'dscr', label: 'DSCR', basis: 'derived', value: engineDscr.value, display: formatMultiple(Number(engineDscr.value.toFixed(2))),
          formula: engineDscr.formula ?? 'NOI ÷ annual debt service', operands: coverageOperands, inputs: ['noi', 'annual_debt_service'],
        }
      : noi != null && debtService != null && debtService > 0
        ? {
            id: 'dscr', label: 'DSCR', basis: 'derived', value: noi / debtService, display: formatMultiple(Number((noi / debtService).toFixed(2))),
            formula: 'NOI ÷ annual debt service', operands: coverageOperands, inputs: ['noi', 'annual_debt_service'],
          }
        : {
            id: 'dscr', label: 'DSCR', display: '—',
            waiting: noi == null ? 'needs NOI' : 'needs annual debt service', inputs: ['noi', 'annual_debt_service'],
          }

  // ── Per unit ──────────────────────────────────────────────────────────────
  // The engine's own per-unit figure first (`loan_per_unit` on a unit count,
  // `loan_per_sf` on an area), labelled Derived with its formula, so the
  // dossier, the grid and the export read one number. The browser's division
  // stands only where the server computed none (UI-03).
  const perUnitFigure = (amount: number, basis: { count: number; noun: string }): string =>
    perUnitText(amount * basis.count, basis)
  const engineLoanPer = unitBasis ? calculated(unitBasis.noun === 'SF' ? 'loan_per_sf' : 'loan_per_unit') : undefined
  const loanPerUnit: LeverageFigure = loan != null && unitBasis
    ? engineLoanPer
      ? {
          id: 'loan_per_unit', label: `Loan / ${unitBasis.noun}`, basis: 'derived', value: engineLoanPer.value,
          display: perUnitFigure(engineLoanPer.value, unitBasis),
          formula: engineLoanPer.formula ?? `loan ÷ ${unitBasis.noun === 'SF' ? 'SF' : `${unitBasis.noun}s`}`,
          operands: `${formatAmount(loan)} ÷ ${unitWords(unitBasis)}`, inputs: ['loan_amount', unitBasis.noun === 'SF' ? 'sf' : 'units'],
        }
      : {
          id: 'loan_per_unit', label: `Loan / ${unitBasis.noun}`, basis: 'derived', value: loan / unitBasis.count,
          display: perUnitText(loan, unitBasis), formula: `loan ÷ ${unitBasis.noun === 'SF' ? 'SF' : `${unitBasis.noun}s`}`,
          operands: `${formatAmount(loan)} ÷ ${unitWords(unitBasis)}`, inputs: ['loan_amount', unitBasis.noun === 'SF' ? 'sf' : 'units'],
        }
    : { id: 'loan_per_unit', label: 'Loan / unit', display: '—', waiting: loan == null ? 'needs a loan amount' : perUnitWaiting(get), inputs: ['loan_amount', 'units', 'sf'] }

  // The basis the loan sits on, per unit: the price paid when the deal states
  // one (what an acquisition is underwritten on), else the as-is value. The
  // engine's `value_per_unit` stands for the value-on-units case.
  const basisAmount = price ?? value
  const basisWords = price != null ? 'price' : 'value'
  const engineValuePer = price == null && unitBasis && unitBasis.noun !== 'SF' ? calculated('value_per_unit') : undefined
  const basisPerUnit: LeverageFigure = basisAmount != null && unitBasis
    ? {
        id: 'basis_per_unit', label: `${price != null ? 'Price' : 'Value'} / ${unitBasis.noun}`, basis: 'derived',
        value: engineValuePer ? engineValuePer.value : basisAmount / unitBasis.count,
        display: engineValuePer ? perUnitFigure(engineValuePer.value, unitBasis) : perUnitText(basisAmount, unitBasis),
        formula: engineValuePer?.formula ?? `${basisWords === 'price' ? 'purchase price' : 'value (as-is)'} ÷ ${unitBasis.noun === 'SF' ? 'SF' : `${unitBasis.noun}s`}`,
        operands: `${formatAmount(basisAmount)} ÷ ${unitWords(unitBasis)}`,
        inputs: [price != null ? 'purchase_price' : 'property_value', unitBasis.noun === 'SF' ? 'sf' : 'units'],
      }
    : { id: 'basis_per_unit', label: 'Price / unit', display: '—', waiting: unitBasis ? 'needs a price or value' : perUnitWaiting(get), inputs: ['purchase_price', 'property_value', 'units', 'sf'] }

  // ── Cap rate ──────────────────────────────────────────────────────────────
  // The stated cap rate as stated; otherwise NOI over the value, or over the
  // price paid — named for which, since the two are different questions.
  const statedCap = points(get('cap_rate'))
  // The label stays "Cap Rate" so it fits a narrow cell; the formula beside
  // it ("NOI ÷ purchase price") says which one it is.
  const capDenominator = value != null ? { amount: value, words: 'value (as-is)' } : price != null ? { amount: price, words: 'purchase price' } : null
  const capRate: LeverageFigure = statedCap != null
    ? held({ id: 'cap_rate', label: 'Cap Rate', basis: 'stated', field: 'cap_rate', value: statedCap, display: formatPoints(statedCap), inputs: ['cap_rate', 'noi', 'property_value'] })
    : noi != null && capDenominator
      ? {
          id: 'cap_rate', label: 'Cap Rate', basis: 'derived', value: (noi / capDenominator.amount) * 100,
          display: formatPoints(Number(((noi / capDenominator.amount) * 100).toFixed(2))),
          formula: `NOI ÷ ${capDenominator.words}`, operands: `${formatAmount(noi)} NOI ÷ ${formatAmount(capDenominator.amount)} ${capDenominator.words}`,
          inputs: ['noi', value != null ? 'property_value' : 'purchase_price'],
        }
      : { id: 'cap_rate', label: 'Cap Rate', display: '—', waiting: noi == null ? 'needs NOI' : 'needs a value or price', inputs: ['noi', 'property_value', 'purchase_price'] }

  // ── Equity and total ──────────────────────────────────────────────────────
  const equityAmount = stack.totalEquity
  const equityShare = stack.equityPct
  const equityImplied = stack.equityImplied
  // A stored preferred or other non-debt layer is not the sponsor's equity.
  const storedEquityLayers = stack.layers.some((layer) => !layer.isDebt && layer.id !== 'residual-equity' && layer.kind !== 'equity')
  const equity: LeverageFigure = equityAmount != null
    ? {
        // The deal's own total_equity, or stored sponsor equity, is the
        // sponsor's; a sum holding preferred equity is named for what it is.
        id: 'equity', label: equityImplied ? 'Implied Equity' : storedEquityLayers && stack.residualSource !== 'equity_field' ? 'Total Equity' : 'Sponsor Equity',
        basis: equityImplied || stack.equityDerived ? 'derived' : 'stated',
        field: !equityImplied && stack.residualSource === 'equity_field' ? 'total_equity' : undefined,
        value: equityAmount,
        display: `${formatAmount(equityAmount)}${equityShare != null ? ` · ${pct1(equityShare)}` : ''}`,
        formula: equityImplied ? impliedFormula(stack) : undefined,
        implied: equityImplied,
        inputs: equityImplied ? impliedInputs(stack) : ['total_equity'],
      }
    : { id: 'equity', label: 'Equity', display: '—', waiting: 'needs a price, cost, value or stated equity', inputs: ['total_equity', 'purchase_price', 'property_value'] }
  const totalCap: LeverageFigure = stack.totalCap != null
    ? {
        id: 'total_cap', label: capitalization?.totalLabel ?? 'Total Capitalization', basis: stack.totalImplied ? 'derived' : 'stated',
        value: stack.totalCap, display: formatAmount(stack.totalCap), implied: stack.totalImplied, inputs: [],
      }
    : { id: 'total_cap', label: 'Total Capitalization', display: '—', inputs: [] }

  // ── What is missing, in one line ─────────────────────────────────────────
  // The denominator the deal's purpose calls for: a refinance is sized on
  // value, an acquisition on price or value, a construction loan on cost.
  const leverageMissing = ltv.display === '—' && ltc.display === '—'
  const denominator = /construct|develop|ground/.test(purpose)
    ? 'a total project cost or value'
    : /acqui|purchase/.test(purpose)
      ? 'a purchase price or value'
      : 'a value (as-is)'
  // ── Coverage the platform computes beside the stated DSCR ────────────────
  // Amortizing coverage (DER-01) and interest-only coverage on the indicative
  // rate (DER-09). Neither replaces the DSCR the deal states; the second rests
  // on a rate the platform priced from a dated SOFR print, so it is a
  // Suggestion and says which print. A value the deal holds is read first (the
  // server writes both onto the record), the served figure where it holds none.
  const coverageExtras: CoverageExtra[] = []
  const amortizing = asNumber(get('dscr_amortizing')) ?? calculated('dscr_amortizing')?.value
  if (amortizing != null) {
    const formula = calculated('dscr_amortizing')?.formula ?? heldDerived('dscr_amortizing') ?? 'NOI / amortizing annual debt service'
    coverageExtras.push({
      id: 'dscr_amortizing', label: 'DSCR (amortizing)', display: formatMultiple(Number(amortizing.toFixed(2))),
      value: amortizing, basis: 'derived', field: 'dscr_amortizing', formula,
      inputs: ['noi', 'annual_debt_service_amortizing'],
    })
  }
  const indicativeCoverage = asNumber(get('dscr_io_indicative')) ?? calculated('dscr_io_indicative')?.value
  if (indicativeCoverage != null) {
    const rate = asNumber(get('rate_indicative'))
    const print = input.basisOf?.('rate_indicative') ?? null
    coverageExtras.push({
      id: 'dscr_io_indicative', label: 'DSCR (IO, indicative)', display: formatMultiple(Number(indicativeCoverage.toFixed(2))),
      value: indicativeCoverage, basis: 'suggested', field: 'dscr_io_indicative',
      formula: calculated('dscr_io_indicative')?.formula ?? heldDerived('dscr_io_indicative') ?? 'NOI / interest-only debt service on the indicative rate',
      note: [rate != null ? `indicative rate ${formatPoints(rate)}` : null, print].filter(Boolean).join(' = ') || undefined,
      inputs: ['noi', 'rate_indicative'],
    })
  }
  const maturity = input.variances?.maturityDate ?? input.variances?.maturity_date
  const maturityVariance: MaturityVariance | null =
    maturity && maturity.stated && maturity.computed && maturity.stated !== maturity.computed
      ? { stated: maturity.stated, computed: maturity.computed, note: maturity.note }
      : null

  const coverageMissing = noi == null
    ? 'NOI to see debt yield and DSCR'
    : dscr.display === '—'
      ? 'annual debt service to see DSCR'
      : null
  let waiting: string | null = null
  if (loan == null && stack.layers.length === 0) {
    waiting = 'Add the loan amount to see leverage and coverage.'
  } else if (stack.seniorUnstated) {
    // Neither line asks for an edit no dossier surface offers: the catalogue
    // has no field for the senior debt ahead of a note, and the stored stack
    // is read-only here (STORED_TRANCHE_REASON). They say what is withheld,
    // and why.
    waiting = 'The senior debt ahead of this note is not stated on this record, so leverage through it is not shown.'
  } else if (stack.debtIncomplete) {
    waiting = 'A debt tranche on the stored capital stack states no amount, so the capitalization is not drawn. The stored stack is not edited in the dossier.'
  } else if (leverageMissing) {
    waiting = `Add ${denominator} to see leverage and equity${coverageMissing ? `; ${coverageMissing}` : ''}.`
  } else if (coverageMissing) {
    waiting = `Add ${coverageMissing}.`
  }

  return {
    loanAmount: loan,
    tranches: stack.layers.map((layer) => ({ ...layer, perUnit: unitBasis ? perUnitText(layer.amount, unitBasis) : undefined })),
    capitalization,
    figures: {
      ltv, ltc, debt_yield: debtYield, dscr, loan_per_unit: loanPerUnit, basis_per_unit: basisPerUnit,
      cap_rate: capRate, equity, total_cap: totalCap,
    },
    waiting,
    coverageExtras,
    maturityVariance,
    perNoun: unitBasis?.noun ?? null,
    disclosure: stack.stackDisclosure,
  }
}

/** The leverage figure to lead with: the LTV where a value is known, else the
 *  LTC — whichever the deal can support, never both blank and a dash first. */
export function headlineLeverage(model: LeverageModel): LeverageFigure {
  const { ltv, ltc } = model.figures
  if (ltv.display !== '—') return ltv
  if (ltc.display !== '—') return ltc
  return ltv
}

function totalLabelFor(stack: CapitalStackModel): string {
  switch (stack.residualSource) {
    case 'uses':
      return stack.usesBasis === 'the purchase price'
        ? 'Purchase Price'
        : stack.usesBasis === 'the total project cost'
          ? 'Total Project Cost'
          : 'Total Uses'
    case 'property_value':
      // A derived value says so through `totalImplied`, beside the label.
      return 'Value (As-Is)'
    case 'equity_field':
    case null:
      return 'Total Capitalization'
    default: {
      const _exhaustive: never = stack.residualSource
      return _exhaustive
    }
  }
}

/** The stack draws more than the value it was closed against: future
 *  advances, which the value at closing does not include. */
function beyondValue(stack: CapitalStackModel): boolean {
  return stack.propertyValue != null && stack.totalCap != null && stack.totalCap > stack.propertyValue + 1
}

/** The implied equity's formula — the one wording Sources & Uses carries too. */
function impliedFormula(stack: CapitalStackModel): string {
  return impliedEquityFormula({ residualSource: stack.residualSource, usesBasis: stack.usesBasis, beyondValue: beyondValue(stack) })
}

function impliedInputs(stack: CapitalStackModel): string[] {
  return impliedEquityInputs({ residualSource: stack.residualSource, layers: stack.layers, uses: stack.uses, totalCost: stack.totalCost })
}

/** "0–75.0%", "75.0–100%": a tranche's place in the stack. */
export function attachmentRange(segment: Pick<CapSegment, 'attach' | 'detach'>): string {
  const start = segment.attach < 0.05 ? '0' : segment.attach.toFixed(1)
  const end = segment.detach > 99.95 ? '100%' : pct1(segment.detach)
  return `${start}–${end}`
}

/** One stated input a person may edit, by catalogue name. */
export type CapitalInput = { name: string; label: string; kind: SlotKind }

/** The catalogue inputs, by name, with the label and slot kind each is edited
 *  as. Every derived figure on the capital surfaces is computed from these. */
export const CAPITAL_INPUTS: Record<string, CapitalInput> = {
  loan_amount: { name: 'loan_amount', label: 'Loan Amount', kind: 'currency' },
  fully_funded_amount: { name: 'fully_funded_amount', label: 'Fully Funded Amount', kind: 'currency' },
  total_equity: { name: 'total_equity', label: 'Sponsor Equity', kind: 'currency' },
  gp_equity: { name: 'gp_equity', label: 'GP Equity', kind: 'currency' },
  lp_equity: { name: 'lp_equity', label: 'LP Equity', kind: 'currency' },
  property_value: { name: 'property_value', label: 'Value (As-Is)', kind: 'currency' },
  purchase_price: { name: 'purchase_price', label: 'Purchase Price', kind: 'currency' },
  total_cost: { name: 'total_cost', label: 'Total Project Cost', kind: 'currency' },
  closing_costs: { name: 'closing_costs', label: 'Closing Costs', kind: 'currency' },
  interest_reserve: { name: 'interest_reserve', label: 'Interest Reserve', kind: 'currency' },
  capex: { name: 'capex', label: 'CapEx Budget', kind: 'currency' },
  noi: { name: 'noi', label: 'NOI (In-Place)', kind: 'currency' },
  annual_debt_service: { name: 'annual_debt_service', label: 'Annual Debt Service', kind: 'currency' },
  units: { name: 'units', label: 'Units / Keys', kind: 'count' },
  sf: { name: 'sf', label: 'Rentable SF', kind: 'count' },
}

export function inputsFor(names: string[]): CapitalInput[] {
  return names.map((name) => CAPITAL_INPUTS[name]).filter((input): input is CapitalInput => Boolean(input))
}

/** The basis a deal's purpose sizes it on, by catalogue name: the purchase
 *  price an acquisition is underwritten on, and the total project cost of a
 *  construction, development or ground-up loan — the purposes the waiting
 *  line names ("Add a purchase price or value …"). */
export function purposeBasisFields(purpose: unknown): string[] {
  const p = String(purpose ?? '').toLowerCase()
  const names: string[] = []
  if (/acqui|purchase/.test(p)) names.push('purchase_price')
  if (/construct|develop|ground/.test(p)) names.push('total_cost')
  return names
}

/** The figures the Overview card leads with, in reading order, when the deal
 *  supports them: leverage, then coverage, then the per-unit basis. */
const CARD_FIGURES: LeverageId[] = ['ltv', 'ltc', 'debt_yield', 'dscr', 'loan_per_unit', 'cap_rate']

/** The figures worth a cell on the Overview card: those the deal's own
 *  figures support. The rest are named by the card's one waiting line. */
export function cardFigures(model: LeverageModel, limit = 6): LeverageFigure[] {
  return CARD_FIGURES.map((id) => model.figures[id]).filter((figure) => figure.basis != null).slice(0, limit)
}

/** The engine's figure for `name`, read off the calculations payload.
 *
 *  The service reads that payload through `toCamelCase`, which rewrites the
 *  keys of every nested object — so the engine's `debt_yield` arrives keyed
 *  `debtYield`, and a lookup by the catalogue name found nothing (dscr, with
 *  no underscore, happened to work). Both spellings are tried. */
export function calculatedFrom(
  derived: Record<
    string,
    { value: number; formula: string | null; fromInputs?: number | null; matchesInputs?: boolean } | undefined
  > | undefined,
): Calculated {
  return (name) => {
    if (!derived) return undefined
    const camel = name.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
    const entry = derived[name] ?? derived[camel]
    return entry && typeof entry.value === 'number' && Number.isFinite(entry.value)
      ? {
          value: entry.value,
          formula: entry.formula ?? null,
          ...(entry.fromInputs !== undefined ? { fromInputs: entry.fromInputs } : {}),
          ...(entry.matchesInputs !== undefined ? { matchesInputs: entry.matchesInputs } : {}),
        }
      : undefined
  }
}
